import { trpc } from '../trpc'
import type { RouterOutputs } from '../trpc'

/** What `auth.me` answers: the live session, rebuilt from `app.users` per request. */
export type SessionUser = RouterOutputs['auth']['me']

/** A section of the plant front, as the API names it. */
export type Section = SessionUser['sections'][number]

export type UseSessionResult = {
  user: SessionUser | undefined
  isLoading: boolean
  isAuthenticated: boolean
  /** Whether the signed-in person's role holds `section`. False while unknown. */
  hasSection: (section: Section) => boolean
  /** Reaches its sections but writes in none. False while unknown. */
  isReadOnly: boolean
}

/**
 * The current session, from `auth.me`. The refresh wrapper handles 401s
 * transparently, so this query only settles to `error` once the silent refresh
 * has ALSO failed — that is, the session is genuinely gone.
 *
 * Two states matter to the guards:
 *
 *   - `isLoading` (`pending`): the first `auth.me` is still in flight and we do
 *     not yet know. Show a loading state, never redirect — the silent refresh
 *     adds a round-trip, and bouncing here flashes the login form on every
 *     reload of a valid session.
 *   - `isError`: the session is over. TanStack Query keeps the last successful
 *     `data` across a failed refetch, so `isAuthenticated` has to be gated on
 *     the error flag too: otherwise a mid-shift expiry leaves a stale user in
 *     place, the guard stays mounted, and every other query on the screen shows
 *     an error instead of the app landing on a clean /login.
 */
export const useSession = (): UseSessionResult => {
  const { data: user, isPending, isError } = trpc.auth.me.useQuery(undefined, {
    // No retry: the refresh wrapper already tried exactly once.
    retry: false,
  })

  const isAuthenticated = !isError && user !== undefined

  return {
    user,
    isLoading: isPending,
    isAuthenticated,
    // Sections are read from the LIVE session, not from a token claim: the API
    // rebuilds them from the row on every request, so a section taken away
    // leaves the bar on the next `auth.me` instead of at the next login.
    hasSection: (section) => isAuthenticated && (user?.sections.includes(section) ?? false),
    isReadOnly: isAuthenticated && user?.readOnly === true,
  }
}
